import { useEffect, useRef, useState } from 'react';
import { LIMITS } from '@tasca/shared';
import type { Channel } from '@tasca/client-sdk';
import { Icon } from './Icon.js';

/**
 * Criar um canal.
 *
 * O `ask()` dava para pedir um nome e mais nada, e por isso só se criavam
 * canais de texto no fim da lista — a app tinha um painel de voz inteiro e
 * nenhuma forma de fazer um segundo canal de voz. Aqui escolhe-se o que é e
 * onde fica, que são as duas perguntas que faltavam.
 */

export type NewChannelDraft = {
  name: string;
  type: 0 | 1 | 2;
  parentId: string | null;
};

const KINDS: { type: 0 | 1 | 2; icon: 'hash' | 'volume' | 'folder'; label: string; hint: string }[] =
  [
    { type: 0, icon: 'hash', label: 'Texto', hint: 'Conversa escrita.' },
    { type: 1, icon: 'volume', label: 'Voz', hint: 'Conversa falada, com vídeo e ecrã.' },
    { type: 2, icon: 'folder', label: 'Categoria', hint: 'Uma gaveta para arrumar canais.' },
  ];

export function NewChannel({
  categories,
  parentId,
  onCreate,
  onClose,
}: {
  categories: Channel[];
  /** Categoria onde cai por omissão — vem de onde o pedido foi feito. */
  parentId: string | null;
  onCreate: (draft: NewChannelDraft) => void;
  onClose: () => void;
}): React.ReactElement {
  const dialog = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<0 | 1 | 2>(0);
  const [parent, setParent] = useState<string | null>(parentId);

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  // Os nomes de canal de texto são em minúsculas e sem espaços — é o que o
  // servidor aceita, e dizê-lo depois de uma recusa era tarde de mais.
  const clean = type === 0 ? name.trim().toLowerCase().replace(/\s+/g, '-') : name.trim();
  const ready = clean.length > 0;

  return (
    <dialog
      ref={dialog}
      className="ask new-channel"
      aria-label="Criar canal"
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === dialog.current) onClose();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          onCreate({ name: clean, type, parentId: type === 2 ? null : parent });
        }}
      >
        <h2>Criar canal</h2>

        <div className="kinds">
          {KINDS.map((kind) => (
            <button
              key={kind.type}
              type="button"
              className={`kind ${type === kind.type ? 'on' : ''}`}
              onClick={() => setType(kind.type)}
              aria-pressed={type === kind.type}
            >
              <Icon name={kind.icon} />
              <span>
                <strong>{kind.label}</strong>
                <small>{kind.hint}</small>
              </span>
            </button>
          ))}
        </div>

        <label>
          Nome
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={type === 1 ? 'balcão' : type === 2 ? 'A tasca' : 'geral'}
            maxLength={LIMITS.channelNameMax}
            autoFocus
          />
        </label>

        {type !== 2 && categories.length > 0 && (
          <label>
            Categoria
            <select value={parent ?? ''} onChange={(e) => setParent(e.target.value || null)}>
              <option value="">Sem categoria</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="ask-actions">
          <button type="button" className="link" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" disabled={!ready}>
            Criar
          </button>
        </div>
      </form>
    </dialog>
  );
}
